import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";

import { AuthProvider, useAuth } from "./AuthContext";
import Splash from "./pages/Splash";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import SessionPlayer from "./pages/SessionPlayer";
import SessionComplete from "./pages/SessionComplete";
import Wishes from "./pages/Wishes";
import Themes from "./pages/Themes";
import History from "./pages/History";
import Settings from "./pages/Settings";
import Streak from "./pages/Streak";
import Privacy from "./pages/Privacy";
import Paywall from "./pages/Paywall";
import Journey from "./pages/Journey";
import Jidnyasa from "./pages/Jidnyasa";
import JidnyasaDashboard from "./pages/JidnyasaDashboard";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminThemes from "./pages/admin/AdminThemes";
import AdminContent from "./pages/admin/AdminContent";
import AdminPrompts from "./pages/admin/AdminPrompts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPreview from "./pages/admin/AdminPreview";
import AdminUploads from "./pages/admin/AdminUploads";
import AdminScenarios from "./pages/admin/AdminScenarios";
import AdminJidnyasa from "./pages/admin/AdminJidnyasa";
import AdminPricing from "./pages/admin/AdminPricing";

function RequireParent({ children, needsOnboarding = false }) {
  const { me, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!me?.user) return <Navigate to="/login" replace />;
  if (!me.profile || !me.child) {
    if (!needsOnboarding) return <Navigate to="/onboarding/parent" replace />;
  }
  return children;
}

function RootGate() {
  const { me, loading } = useAuth();
  if (loading) return <Splash />;
  if (me?.user) return <Navigate to="/home" replace />;
  return <Splash />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/onboarding/*" element={
            <RequireParent needsOnboarding><Onboarding /></RequireParent>
          } />
          <Route path="/home" element={<RequireParent><Home /></RequireParent>} />
          <Route path="/session" element={<RequireParent><SessionPlayer /></RequireParent>} />
          <Route path="/session/complete" element={<RequireParent><SessionComplete /></RequireParent>} />
          <Route path="/wishes" element={<RequireParent><Wishes /></RequireParent>} />
          <Route path="/themes" element={<RequireParent><Themes /></RequireParent>} />
          <Route path="/history" element={<RequireParent><History /></RequireParent>} />
          <Route path="/streak" element={<RequireParent><Streak /></RequireParent>} />
          <Route path="/settings" element={<RequireParent><Settings /></RequireParent>} />
          <Route path="/paywall" element={<RequireParent><Paywall /></RequireParent>} />
          <Route path="/journey" element={<RequireParent><Journey /></RequireParent>} />
          <Route path="/jidnyasa" element={<Jidnyasa />} />
          <Route path="/jidnyasa/dashboard" element={<RequireParent><JidnyasaDashboard /></RequireParent>} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="themes" element={<AdminThemes />} />
            <Route path="content/:kind" element={<AdminContent />} />
            <Route path="scenarios" element={<AdminScenarios />} />
            <Route path="jidnyasa" element={<AdminJidnyasa />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="prompts" element={<AdminPrompts />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="preview" element={<AdminPreview />} />
            <Route path="uploads" element={<AdminUploads />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
